import { useEffect } from 'react';

import create from 'zustand';

type MenuOpenStore = {
  count: number;
};

const defaultState: MenuOpenStore = {
  count: 0,
};

const useStore = create<MenuOpenStore>(() => defaultState);

const increment = () => useStore.setState((s) => ({ count: s.count + 1 }));
const decrement = () => useStore.setState((s) => ({ count: Math.max(0, s.count - 1) }));

export const menuOpenStore = {
  useStore,
  useIsAnyMenuOpen: () => useStore((s) => s.count > 0),
};

// A context menu (a ctrl+click on macOS) can take the release, so it ends the press too.
const PRESS_END_EVENTS = ['pointerup', 'pointercancel', 'contextmenu'] as const;

// A select can close on pointerdown while an InlineSidebar only decides on the
// pointerup of that same press whether it was an outside click, so the menu
// stays counted until the press has finished dispatching.
const decrementAfterPointerRelease = () => {
  const release = () => {
    PRESS_END_EVENTS.forEach((type) => window.removeEventListener(type, release, true));
    setTimeout(decrement);
  };
  PRESS_END_EVENTS.forEach((type) => window.addEventListener(type, release, true));
};

export const useTrackMenuOpen = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    increment();

    let isPointerDown = false;
    const onPointerDown = (event: PointerEvent) => {
      if (event.button === 0) {
        isPointerDown = true;
      }
    };
    const onPointerEnd = () => {
      isPointerDown = false;
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    PRESS_END_EVENTS.forEach((type) => window.addEventListener(type, onPointerEnd, true));

    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      PRESS_END_EVENTS.forEach((type) => window.removeEventListener(type, onPointerEnd, true));
      if (isPointerDown) {
        decrementAfterPointerRelease();
      } else {
        decrement();
      }
    };
  }, [isOpen]);
};
